import React, { useEffect, useContext, useMemo } from "react"
import { useParams } from "react-router-dom"

import { Button, Box } from "@mui/material"
import { FaIcon } from "../../../../../mp-core/icons"
import DropdownMenu, {
  DropdownMenuOptions
} from "../../../../generic-shared-components/components/DropdownMenu"

import { LinkType, MainNavLinksType } from ".."
import { AppContext, ActionTypeEnums } from "../../../../../mp-core/context"
import { ProgramsContext } from "../ProgramsContext"
import { studyHallCourseNamesByProgram } from "../../../../../mp-utils/generic-utils"

import { useStyles } from "./styles"

type MainNavParamsType = {
  page: string
  module: string
  program: string
  assessmentId?: string
  subcourseId?: string
  lessonId?: string
}

interface ProgramsDropDownProps {
  id?: string
  links: MainNavLinksType
}

const ProgramsDropDown = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<ProgramsDropDownProps>
>((props, ref) => {
  const { id, links } = props
  const params = useParams<MainNavParamsType>()
  const { page, module } = params

  const {
    currentProgram,
    currentProgramCourse,
    programsAndCourses,
    currentProgramSetter
  } = useContext(ProgramsContext)

  const programDisplayName =
    currentProgramCourse?.programInfo?.displayName ??
    currentProgram?.toUpperCase() ??
    "--"

  const { dispatch } = useContext(AppContext)

  const { classes } = useStyles()

  useEffect(() => {
    if (currentProgram) {
      dispatch({
        type: ActionTypeEnums.UpdateParams,
        payload: { currentProgram }
      })
    }
  }, [currentProgram])

  const productsLinks = useMemo(
    () =>
      [...(links?.products || []), links?.eBooks]?.filter(link => link) ?? [],
    [links]
  )

  const programsLinks = useMemo(
    () =>
      productsLinks
        ?.map(link => link?.children?.map(child => child?.children))
        ?.flat(2)
        ?.filter(link => link)
        ?.reduce<Record<string, LinkType[]>>((programs, link) => {
          if (!programs[link.key]) {
            programs[link.key] = []
          }

          programs[link.key].push(link)

          return programs
        }, {}),
    [productsLinks]
  )

  const isProfilePage = page === "profile"
  const isSingleProgram = Object.keys(programsLinks ?? {}).length <= 1
  const showDropdown = isSingleProgram && !isProfilePage

  const options: DropdownMenuOptions[] = useMemo(
    () =>
      Object.entries<LinkType[]>(programsLinks)
        .filter(([_, programLinks]) =>
          programLinks.some(link => link.url && link.key)
        )
        .filter(([program, _]) => {
          // If we're in Study Hall context, only show programs that are supported
          if (page === "truscore" && module === "study-hall") {
            return studyHallCourseNamesByProgram.hasOwnProperty(
              program.toLowerCase()
            )
          }
          return true
        })
        .map(([program, programLinks]) => {
          const link =
            productsLinks
              // Try to find program link to the same page and module.
              .find(link => link.key === page)
              // If no module, try to find by page name again (e.g. eBooks).
              ?.children?.find(link => link.key === (module ?? page))
              // If module found, try to find by program.
              // Default to first program link.
              ?.children?.find(link => link.key === program) ?? programLinks[0]

          return {
            id: link.name,
            label:
              programsAndCourses?.find(
                programCourse =>
                  programCourse.program.toLowerCase() === program.toLowerCase()
              )?.programInfo?.displayName ?? program?.toUpperCase(),
            selected: currentProgram === program,
            href: link.url,
            action: () => currentProgramSetter(program),
            "data-automation-id": "program-list"
          }
        })
        .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label)),
    [
      programsLinks,
      productsLinks,
      currentProgram,
      page,
      module,
      programsAndCourses
    ]
  )

  return (
    <Box ref={ref} className={classes.root}>
      <DropdownMenu
        id="programs-dropdown"
        anchor={
          <Button
            id={id}
            size="large"
            disabled={showDropdown}
            aria-disabled={showDropdown ? true : undefined}
            variant="contained"
            disableRipple
            className={classes.mainButton}
            endIcon={
              !showDropdown && (
                <FaIcon
                  solid
                  name="chevron-down"
                  className={classes.mainButtonIcon}
                />
              )
            }
            data-automation-id="program-dropdown"
          >
            {programDisplayName}
          </Button>
        }
        options={options}
        disablePortal
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        classes={{
          paper: classes.paper,
          menuList: classes.menuList,
          menuItem: classes.menuListItem
        }}
      />
    </Box>
  )
})

export default ProgramsDropDown
